import PageTitle from 'components/PageTitle';

import Section from '../components/Section';
import { InlineClippedImage } from '../components/InlineClippedImage';
import ContentTypeNav from 'components/ContentTypeNav';

import signature from '../assets/images/tg-signature.png';

import Connell_2008_Woods_and_Roses from '../assets/images/content/Connell_2008_Woods_and_Roses.jpg';
import Connell_2015_Undercurrent from '../assets/images/content/Connell_2015_Undercurrent.jpg';
import Connell_2003_Chamomile from '../assets/images/content/Connell_2003_Chamomile.jpg';
import Connell_2002_Kitchen_Tension from '../assets/images/content/Connell_2002_Kitchen_Tension.jpg';
import Connell_2002_Picnic from '../assets/images/content/Connell_2002_Picnic.jpg';
import stay_well_be_kind from '../assets/images/stay_well_be_kind.svg';
import Connell_2008_Backgammon from '../assets/images/content/Connell_2008_Backgammon.jpg';

const Q1LetterToShareholders = () => {
	return (
		<>
			<PageTitle title="Letter to Shareholders" />

			<div id="introduction" className="bg-[#D1E5DF] py-12 md:py-24">
				<div className="container max-w-[1068px]">
					<div className="md:max-w-[873px]">
						<ContentTypeNav type="letter" />
						<p className="font-late-serif text-[1.375rem] md:text-[1.65rem] leading-[1.6] mb-6">I would characterize the first quarter of 2025 as very successful and, more importantly, stable. With all that is going on outside of our walls related to foreign trade and tariffs, we have really focused on trying to maintain stable rates for our customers and taking care of them in their time of need. As I had stated in the last investor relations call, we have a team that crunches the data on anticipated magnitude and severity and what increased tariffs might mean to our loss costs. We have a variety of scenarios that we are running and, as it plays out in real time, we plan to react swiftly should we need to.</p>
					</div>
				</div>
			</div>

			<div id="leading-brand" className="py-0 md:py-24">
				<div className="grid md:grid-cols-[1fr_1fr] gap-12 container">
					<div className="grid">
						<div className="md:max-w-[520px] ml-auto">
							<p>For the quarter, companywide net premiums written (NPW) grew 17%, compared to the first quarter last year, and the combined ratio (CR) was 86.0. We also had very impressive policies in force (PIF) growth, with nearly 5.5 million more PIFs than the end of the first quarter last year, bringing our total PIF count to over 36 million at March 31, 2025. Even with this level of unit growth, because we hire in advance of need in claims and our call centers, this massive growth hasn’t strained our ability to provide high-quality customer service.</p>

							<p>In Personal Lines (auto, special lines, and property), the first quarter 2025 delivered another period of exceptional growth and profitability. The quarter ended with over 35 million PIFs, representing 18% year-over-year (YOY) growth. NPW grew 20% YOY at a CR of 85.7. During the first quarter, our personal vehicle businesses continued to experience both strong policy and premium growth, while personal property remained focused on profitable growth in less volatile areas and reducing concentration in regions more prone to catastrophes. </p>
						</div>
					</div>
					<div className="md:max-w-[583px] ml-auto">
						<div>
							<InlineClippedImage
								src={Connell_2008_Woods_and_Roses}
								alt="Art"
								dimensions={{
									width: 1920,
									height: 1440,
								}}
								clipPaths={[
									{
										initial: {
											x: '45%',
											y: '3%',
											width: '45%',
										},
										expanded: {
											x: '0%',
											y: '0%',
											width: '100%',
										},
									},
								]}
							/>
						</div>
					</div>
				</div>
				<div className="grid md:grid-cols-[1fr_1fr] gap-12 container">
					<div className="grid">
						<div className="md:max-w-[520px] ml-auto mt-12 md:mt-0">
							<p>We began 2025 open for business in almost all states for our personal auto product. Since the first quarter of the year is peak shopping season, we invested heavily in advertising and used agent reward incentives to drive consumer shopping. As a result of these efforts, new personal auto applications grew 32% YOY. While rate activity was minimal during the first quarter, we are closely monitoring the tariffs that might impact the auto sector, as well as other aspects of our business, and are regularly modeling how those could impact our loss costs, the supply chain, the availability of parts, and general inflation.</p>
						</div>
						<div className="md:max-w-[583px] mx-auto mt-12 md:mt-auto">
							<InlineClippedImage
								src={Connell_2015_Undercurrent}
								alt="Art"
								dimensions={{
									width: 1920,
									height: 1440,
								}}
								clipPaths={[
									{
										initial: {
											x: '55%',
											y: '26%',
											width: '40%',
										},
										expanded: {
											x: '0%',
											y: '0%',
											width: '100%',
										},
									},
								]}
							/>
						</div>
					</div>
					<div>
						<div className="md:max-w-[520px]">
							<p>Our latest auto product offering, model 8.9, was in 21 states that represented 40% of companywide personal auto premium, on a trailing 12-month basis, at the end of the first quarter 2025. The 8.9 model contains new and expanded use of external data and introduces Progressive vehicle protection, a new mechanical breakdown coverage that runs concurrent with Progressive auto insurance to help consumers manage the cost of unforeseen vehicle repairs. Our preferred business is showing the most promising response to the model. Overall, we are seeing favorable conversion results in both the agency and direct auto channels. At quarter end, continuous monitoring in our Snapshot® product model 5.0, which includes a feature in the Progressive app that can detect major accidents and quickly connect customers to towing and emergency services, was deployed in 41 states that represented 78% of our trailing 12-month personal auto NPW (excluding California).</p>

							<p>Personal property ended the first quarter with a CR of 87.2, which is 6.2 points better than first quarter 2024, and includes about 3.7 points of favorable prior accident year development, primarily related to prior-year weather events. We ended the quarter with over 3.5 million PIFs, which are up 11% over first quarter 2024, driven primarily by growth in our renters business. NPW were flat, compared to first quarter 2024, driven by a shift in mix towards renters, which have lower average written premiums, and our continued actions to deliberately slow growth and non-renew policies in certain geographic regions where we are de-risking.</p>

							<p>We continue to focus on improving profitability and reducing exposure in markets more susceptible to catastrophic weather events. We also continue to prioritize insuring lower-risk properties (e.g., new construction, existing homes with newer roofs), accepting new business for homeowners/condo products only when bundled with a Progressive personal auto policy, where permitted, and exiting the non-owner-occupied home market. For our homeowners/condo products, PIFs were up about 14% in states where we are looking to expand our footprint and down 12% in higher-risk states where we are looking to reduce our exposure.</p>
						</div>
					</div>
				</div>
			</div>

			<div className="container py-0 md:py-24">
				<img src={stay_well_be_kind} alt="stay_well_be_kind" className="" />
			</div>

			<Section backgroundColor="#F1F7F6">
				<div className="md:grid md:grid-cols-[1fr_1fr] gap-12 px-4 sm:px-6">
					<div className="md:max-w-[520px] ml-auto ">
						<p>Advancing our segmentation capabilities in personal property is a continued focus and, through the end of the first quarter 2025, we had elevated our next generation product models of 5.0 or higher in 22 states that represented 62% of our trailing 12-month homeowners product NPW. Key features include expanded peril rating and the introduction of new rating variables. We also continued to take rate where needed and increased our personal property rates about 2%, in the aggregate, in the first quarter 2025, bringing the trailing 12-month aggregate rate increase to about 15%.</p>
					</div>
				</div>
				<div className="md:grid md:grid-cols-[1fr_1fr] gap-12 px-4 sm:px-6">
					<div className="md:max-w-[520px] ml-auto">
						<p>Our Commercial Lines (CL) business finished the quarter with NPW growth of 5% at an 87.5 CR. Premium growth in the business auto and contractor business market targets (BMT) and the transportation network company business were each a notable contributor to overall CL growth during the quarter.</p>
					</div>
					<div className="md:max-w-[520px] grid">
						<div className="mt-auto">
							<p>Core commercial auto NPW growth was flat for the quarter despite having positive PIF growth due to a shift in the mix of business. We continued to see strong growth in our business auto and contractor BMTs during the quarter, driven by quote and conversion improvements, but also continued to face challenges in our for-hire trucking and for-hire specialty BMTs, which have higher premiums per policy than our business auto and contractor BMTs.</p>

							<p>The commercial auto industry (excluding Progressive) ended the year at an estimated 111.4 CR. Industry data indicates competitors are taking rate increases. Council of Insurance Agents & Brokers (CIAB) survey data from the fourth quarter 2024, shows commercial auto rate increases are the highest across all the commercial lines CIAB monitors and were consistently in the high single-digits during 2024. We expect our rates to become more competitive as industry rate levels increase.</p>
						</div>
					</div>
				</div>
			</Section>

			<div>
				<div className="grid grid-cols-1 gap-4 items-stretch mt-24 mb-8 md:grid-cols-3">
					<img src={Connell_2003_Chamomile} alt="Connell_2024_Still_Summer" className="w-full h-auto" />
					<img src={Connell_2002_Kitchen_Tension} alt="Connell_2002_Kitchen_Tension" className="w-full h-auto" />
				</div>
				<div className="md:grid md:grid-cols-[56%_1fr] gap-12">
					<div className="md:max-w-[534px] order-1 ml-auto py-12 px-4 sm:px-6">
						<p>On the capital management side, we came into 2025 with a robust capital position and that has proved beneficial as we continued to grow during the first quarter. Even as we experienced turbulent markets and strong premium growth during the quarter, we were able to generate significant capital through positive cash flows and investment returns and ended the quarter with a debt-to-total capital ratio of 19.2%, which remains near the lower end of our historical range. We believe that the combination of operational momentum and our strong capital position will continue to allow us to pursue robust profitable growth.</p>
						<p>In the first quarter of 2025, our investment portfolio saw a return of 2.2%. Macroeconomic uncertainty rose significantly during the quarter, impacting market returns. Our fixed-income return was 2.5% for the quarter as yields dropped. Our equity portfolio returned -5.0% as market prices decreased significantly versus a 9.9% increase in first quarter 2024, contributing to a substantial decline in this component of net income. Our investment portfolio remains in a conservative posture and is highly liquid with 56% of our portfolio in short-term securities or treasury bonds.</p>
					</div>
				</div>
				<div className="md:grid md:grid-cols-[56%_1fr] gap-12 px-4 sm:px-6">
					<div className="order-1 md:grid">
						<div className="md:max-w-[563px] h-[422.25px] mr-auto">
							<InlineClippedImage
								src={Connell_2008_Backgammon}
								dimensions={{
									width: 1920,
									height: 1440,
								}}
								clipPaths={[
									{
										initial: {
											x: '56%',
											y: '4%',
											width: '38%',
										},
										expanded: {
											x: '0%',
											y: '0%',
											width: '100%',
										},
									},
								]}
							/>
						</div>
					</div>
					<div className="max-w-[550px] order-2 mr-auto">
						<p>As I often do, I like to share comments from our customers. This one felt really special because we spend a lot of time making sure each customer feels confident that they are getting great coverage, service, and rates. We seek to respect each customer as an individual, and not just another number, and to build that care into all aspects of our communications.</p>

						<p>Courtney’s comments summarize how we want every interaction to be, which is easy, clear, and straightforward.</p>

						<p className="text-[#F05860] font-late-serif">Dear Progressive Team,</p>
						<p className="text-[#F05860] font-late-serif">I just wanted to take a moment to say how easy and stress-free it was to purchase my car and renters insurance through your website. I was expecting a long, complicated process, but instead, everything was super straightforward. The site was simple to navigate, loved the customizable options in the dropdowns, and I really appreciated how clear all the coverage options were—it made choosing the right policy an absolute breeze.</p>
						<p className="text-[#F05860] font-late-serif">Sure, I can talk about how “Getting a quote was quick” and “Bundling my policies saved me both time and money”... which is always a huge plus, BUT what made your company stand out is - I never once felt lost or confused during the process, which isn&apos;t always the case when dealing with insurance!</p>
						<p className="text-[#F05860] font-late-serif">So, I just wanted to send a quick “thank you” for making everything so easy. I’ll definitely be recommending Progressive to friends and family!</p>
						<p className="text-[#F05860] font-late-serif">Tired mom of two 16yr olds, a 15yr old, and 14yr old</p>
						<p className="text-[#F05860] font-late-serif">Courtney</p>

						<p>We head into the second quarter 2025 excited about the opportunities that lie ahead of us as we aim to remain nimble as events of the world unfold.</p>

						<p className="mb-6">Stay well and be kind to others,</p>
						<img src={signature} className="max-w-[255px]" alt="" />
						<p>
							Tricia Griffith <br /> President and Chief Executive Officer
						</p>
					</div>
				</div>
			</div>

			<div className="container grid py-24">
				<div className="max-w-[1114px] mr-auto">
					<InlineClippedImage
						src={Connell_2002_Picnic}
						dimensions={{
							width: 1920,
							height: 1440,
						}}
						clipPaths={[
							{
								initial: { x: '51%', y: '5%', width: '44%' },
								expanded: { x: '0%', y: '0%', width: '100%' },
							},
						]}
					/>
				</div>
			</div>
		</>
	);
};

export default Q1LetterToShareholders;
